/**
 * The public page of a member link, for someone who cannot open the member page: a collection link a creator
 * shared from the address bar, opened while logged out or by a signed-in non-member.
 */

import { getPublicCollectionByCollectionId } from '@/api/collection'

// Member routes whose public twin is the same path behind /u/:username (routes/public.js mirrors them)
const TWINNED_ROUTES = new Set([
  '/collections/:id',
  '/collections/:id/bookmarks/:bookmarkId',
  '/collections/:id/notes/:noteId',
])

/**
 * @param {Object} route - a resolved route
 * @returns {Boolean} whether the route has a public twin
 */
export const hasPublicTwin = route => TWINNED_ROUTES.has(route.matched.at(-1)?.path)

/**
 * Where a member link sends someone who cannot open it: the collection's public page, with the same path, query and
 * hash behind the owner's username.
 *
 * @param {Object} route - the member route (a guard's `to`, or the current route)
 * @returns {Promise<Object|null>} a location for next() or router.replace; null when the route has no public twin,
 *   the collection is not public or the lookup fails, so the caller carries on as before
 */
export async function publicVersionLocation(route) {
  if (!hasPublicTwin(route)) return null

  try {
    const { owner } = await getPublicCollectionByCollectionId(route.params.id)
    if (!owner?.username) return null
    return { path: `/u/${owner.username}${route.path}`, query: route.query, hash: route.hash }
  } catch {
    return null
  }
}
